// src/components/Header/Header.tsx
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import { RootState } from '../../redux/store';
import { clearUser } from '../../redux/slices/userSlice';
import Avatar from '../Avatar/Avatar';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  justifyContent: 'space-between',
  backgroundColor: '#ffffff',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
  },
}));

const NavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '& .MuiButton-root': {
    color: theme.colors.deepBlue,
    '&:hover': {
      backgroundColor: 'rgba(9, 13, 46, 0.06)',
    },
    '&.active': {
      backgroundColor: 'rgba(9, 13, 46, 0.10)',
    },
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: '#ffffff',
}));

const BetaText = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 400,
  fontStyle: 'italic',
  fontFamily: '"Euclid Circular A", sans-serif',
  color: theme.colors.lavender,
  marginLeft: theme.spacing(0.25),
  lineHeight: 1,
  position: 'relative',
  top: '-0.83em',
  verticalAlign: 'super',
}));

const navItems = [
  // { text: 'Home', path: '/', icon: <HomeIcon /> },
  { text: 'Chat', path: '/chat', icon: <ChatIcon /> },
  { text: 'Advisory', path: '/advisory', icon: <InsightsOutlined /> },
];

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { t } = useUntypedTranslation();

  // Check if in staging environment by looking at Firebase project ID
  const isStaging = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'gaialogic-staging';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);
  const handleSignOut = () => {
    dispatch(clearUser());
    handleCloseMenu();
    setDrawerOpen(false);
  };
  const handleSignIn = () => {
    navigate('/signin');
    setDrawerOpen(false);
  };
  const handleGoToChat = () => {
    navigate('/chat');
    handleCloseMenu();
    setDrawerOpen(false);
  };

  const renderNavContent = (isMobile = false) => (
    <NavContainer>
      {!isMobile &&
        navItems.map(item => (
          <Button
            key={item.text}
            component={Link}
            to={item.path}
            className={location.pathname === item.path ? 'active' : ''}
          >
            {t(`navigation.${item.text.toLowerCase()}`)}
          </Button>
        ))}
      {isAuthenticated && user ? (
        <>
          <IconButton onClick={handleMenu}>
            <Avatar
              src={user.avatar || ''}
              alt={user.username || 'User'}
              name={user.username || 'User'}
            />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled sx={{ opacity: 0.7 }}>
              {user?.username || 'User'}
            </MenuItem>
            <MenuItem
              disabled
              sx={{
                opacity: 0.6,
                fontSize: '0.8rem',
                py: 0, // Reduce padding
                minHeight: 'auto', // Reduce min height
              }}
            >
              {user?.email || 'No email'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleGoToChat}>{t('navigation.chat')}</MenuItem>
            <MenuItem onClick={handleSignOut}>{t('navigation.signout')}</MenuItem>
          </Menu>
        </>
      ) : (
        <Button
          variant="outlined"
          onClick={handleSignIn}
          sx={{
            color: theme.colors.deepBlue,
            borderColor: 'rgba(9, 13, 46, 0.25)',
            '&:hover': {
              borderColor: theme.colors.deepBlue,
              backgroundColor: 'rgba(9, 13, 46, 0.06)',
            },
          }}
        >
          {t('navigation.signin')}
        </Button>
      )}
      <LanguageSwitcher />
    </NavContainer>
  );

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#ffffff',
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.colors.snowSecondary}`,
      }}
    >
      <StyledToolbar>
        {isMobile && (
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ color: theme.colors.deepBlue }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component={Link}
            to={isAuthenticated ? '/chat' : '/'}
            sx={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <picture>
              {isMobile ? (
                // Mobile: Bildmarke (mark only) in deep blue on white header
                <>
                  <source srcSet="/icons/icon-bildmarke-deepblue.svg" type="image/svg+xml" />
                  <source srcSet="/icons/icon-96x96-deepblue.png" type="image/png" />
                  <img
                    src="/icons/icon-96x96-deepblue.png"
                    alt="gaialogic"
                    style={{ height: '2rem', width: 'auto' }}
                  />
                </>
              ) : (
                // Desktop: deep-blue Wortbildmarke on white header
                <>
                  <source srcSet="/logos/logo-deepblue-wortbildmarke.svg" type="image/svg+xml" />
                  <source srcSet="/logos/logo-deepblue-wortbildmarke-large.png" type="image/png" />
                  <img
                    src="/logos/logo-deepblue-wortbildmarke-large.png"
                    alt="gaialogic"
                    style={{ height: '2.75rem', width: 'auto', display: 'block' }}
                  />
                </>
              )}
            </picture>
          </Box>
          <BetaText>beta</BetaText>
        </Box>
        {!isMobile && renderNavContent()}

        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 250 }}>
            <DrawerHeader>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <picture>
                  <source srcSet="/logos/logo-deepblue-wortbildmarke.svg" type="image/svg+xml" />
                  <source srcSet="/logos/logo-deepblue-wortbildmarke-large.png" type="image/png" />
                  <img
                    src="/logos/logo-deepblue-wortbildmarke-large.png"
                    alt="gaialogic"
                    style={{ height: '2.5rem', width: 'auto', display: 'block' }}
                  />
                </picture>
                <BetaText sx={{ fontSize: '0.875rem' }}>beta</BetaText>
              </Box>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ color: theme.colors.deepBlue }}
              >
                <CloseIcon />
              </IconButton>
            </DrawerHeader>
            <Divider />
            {isAuthenticated && user && (
              <>
                <ListItem sx={{ opacity: 0.7 }}>
                  <ListItemIcon>
                    <Avatar
                      src={user.avatar || ''}
                      alt={user.username || 'User'}
                      name={user.username || 'User'}
                    />
                  </ListItemIcon>
                  <ListItemText primary={user.username || 'User'} />
                </ListItem>
                <Divider />
              </>
            )}
            <List>
              {navItems.map(item => (
                <ListItem
                  button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(`navigation.${item.text.toLowerCase()}`)} />
                </ListItem>
              ))}
              <Divider />
              <ListItem button onClick={isAuthenticated ? handleSignOut : handleSignIn}>
                <ListItemText
                  primary={t(isAuthenticated ? 'navigation.signout' : 'navigation.signin')}
                />
              </ListItem>
              <ListItem>
                <LanguageSwitcher />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </StyledToolbar>
    </AppBar>
  );
};

export default Header;
